import type { LocationQuery } from "vue-router";
import type { PaymentOutcome, PaymentReturnStatus } from "./types";

const successfulResultCodes = new Set(["authorised", "received"]);
const pendingResultCodes = new Set(["pending"]);
const failedResultCodes = new Set(["cancelled", "refused", "error"]);

export function paymentReturnStatusFromQuery(query: LocationQuery): PaymentReturnStatus {
  const resultCode = firstQueryValue(query.resultCode ?? query.status ?? query.paymentStatus);
  const merchantReference = firstQueryValue(query.merchantReference ?? query.reference ?? query.orderId);
  const normalizedResultCode = resultCode?.toLowerCase();

  if (normalizedResultCode && successfulResultCodes.has(normalizedResultCode)) {
    return {
      state: "success",
      title: "Zahlung erfolgreich",
      description: "Deine Token Bestellung wurde erfolgreich bezahlt.",
      resultCode,
      merchantReference,
    };
  }

  if (normalizedResultCode && failedResultCodes.has(normalizedResultCode)) {
    return {
      state: "error",
      title: "Zahlung nicht abgeschlossen",
      description: "Der Zahlungsanbieter hat die Zahlung abgelehnt, abgebrochen oder mit einem Fehler beendet. Bitte starte den Checkout erneut.",
      resultCode,
      merchantReference,
    };
  }

  if (normalizedResultCode && pendingResultCodes.has(normalizedResultCode)) {
    return {
      state: "pending",
      title: "Zahlung in Bearbeitung",
      description: "Der Zahlungsanbieter verarbeitet deine Zahlung noch. Der finale Status wird nach der Bestätigung aktualisiert.",
      resultCode,
      merchantReference,
    };
  }

  if (firstQueryValue(query.redirectResult)) {
    // A redirectResult alone is not the final payment result; the webhook/backend remains the source of truth.
    return {
      state: "pending",
      title: "Zahlungsrückkehr empfangen",
      description: "Der Zahlungsanbieter hat dich zum Portal zurückgeleitet. Der finale Zahlungsstatus wird nach der Backend-Bestätigung angezeigt.",
      resultCode,
      merchantReference,
    };
  }

  return {
    state: "error",
    title: "Zahlungsstatus unbekannt",
    description: "Die Rückkehr vom Zahlungsanbieter enthält keinen auswertbaren Zahlungsstatus. Bitte prüfe deine Bestellung oder starte den Checkout erneut.",
    resultCode,
    merchantReference,
  };
}

export function paymentSessionIdFromQuery(query: LocationQuery): string | undefined {
  return firstQueryValue(
    query.paymentSessionId ??
      query.sessionId ??
      query.paymentProviderSessionId,
  );
}

function firstQueryValue(value: LocationQuery[string]): string | undefined {
  if (Array.isArray(value)) return value.find((item): item is string => typeof item === "string" && item.length > 0);
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export interface ParsedPaymentReturn {
  outcome: PaymentOutcome;
  resultCode?: string;
  orderReference?: string;
}

/** Maps an Adyen-style return query into a provider-agnostic result. */
export function parsePaymentReturn(query: LocationQuery): ParsedPaymentReturn {
  const status = paymentReturnStatusFromQuery(query);
  const outcome: PaymentOutcome =
    status.state === "success"
      ? "success"
      : status.state === "pending"
        ? "pending"
        : status.resultCode
          ? "failed"
          : "unknown";
  return {
    outcome,
    resultCode: status.resultCode,
    orderReference: status.merchantReference,
  };
}
